import React, { useState, useCallback, useEffect } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { Routes as RouteList } from '../../app/routes';
import { useCommandPaletteSearch } from './use-command-palette-search';
import { useRecentItems, RecentItem } from './use-recent-items';
import {
  navigationCommands,
  createCommands,
  actionCommands,
  CommandContext,
} from './commands';
import { ISearchResultItem, SearchEntityType } from '@topshots/core-models';
import {
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  CurrencyPoundIcon,
  GiftIcon,
  UserIcon,
  TagIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import styles from './command-palette.module.css';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const entityTypeLabels: Record<SearchEntityType, string> = {
  event: 'Events',
  order: 'Orders',
  product: 'Products',
  user: 'Users',
  coupon: 'Coupons',
  enquiry: 'Enquiries',
};

const getRouteForResult = (item: ISearchResultItem): string => {
  switch (item.type) {
    case 'event':
      return RouteList.EVENT.createLink?.(item._id) ?? '';
    case 'order':
      return RouteList.ORDER.createLink?.(item._id) ?? '';
    case 'product':
      return RouteList.PRODUCT.createLink?.(item._id) ?? '';
    case 'user':
      return RouteList.USER.createLink?.(item._id) ?? '';
    case 'coupon':
      return RouteList.COUPON.createLink?.(item._id) ?? '';
    case 'enquiry':
      return `/enquiries/${item._id}`;
    default:
      return '/';
  }
};

const getRecentRoute = (item: RecentItem): string => {
  switch (item.type) {
    case 'event':
      return RouteList.EVENT.createLink?.(item.id) ?? '';
    case 'order':
      return RouteList.ORDER.createLink?.(item.id) ?? '';
    case 'product':
      return RouteList.PRODUCT.createLink?.(item.id) ?? '';
    case 'user':
      return RouteList.USER.createLink?.(item.id) ?? '';
    case 'coupon':
      return RouteList.COUPON.createLink?.(item.id) ?? '';
    case 'enquiry':
      return `/enquiries/${item.id}`;
    default:
      return '/';
  }
};

const EntityIcon = ({ type }: { type: string }) => {
  const size = 16;
  switch (type) {
    case 'event':
      return <CalendarDaysIcon height={size} width={size} />;
    case 'order':
      return <CurrencyPoundIcon height={size} width={size} />;
    case 'product':
      return <GiftIcon height={size} width={size} />;
    case 'user':
      return <UserIcon height={size} width={size} />;
    case 'coupon':
      return <TagIcon height={size} width={size} />;
    case 'enquiry':
      return <ChatBubbleOvalLeftEllipsisIcon height={size} width={size} />;
    default:
      return null;
  }
};

export const CommandPalette = ({ open, onOpenChange }: CommandPaletteProps) => {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { grouped, isLoading, hasQuery } = useCommandPaletteSearch(search);
  const { recents, addRecent } = useRecentItems();

  const ctx: CommandContext = {
    navigate: (path: string) => {
      navigate(path);
      onOpenChange(false);
    },
    dispatch: (action) => {
      dispatch(action);
      onOpenChange(false);
    },
  };

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const handleSelectResult = useCallback(
    (item: ISearchResultItem) => {
      addRecent({
        type: item.type,
        id: item._id,
        label: item.label,
        subtitle: item.subtitle,
      });
      navigate(getRouteForResult(item));
      onOpenChange(false);
    },
    [addRecent, navigate, onOpenChange]
  );

  const handleSelectRecent = useCallback(
    (item: RecentItem) => {
      addRecent(item);
      navigate(getRecentRoute(item));
      onOpenChange(false);
    },
    [addRecent, navigate, onOpenChange]
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command Palette"
      className={styles.dialog}
      overlayClassName={styles.overlay}
      contentClassName={styles.content}
      shouldFilter={!hasQuery}
    >
      <div className={styles.inputWrapper}>
        <MagnifyingGlassIcon
          height={20}
          width={20}
          className={styles.searchIcon}
        />
        <Command.Input
          value={search}
          onValueChange={setSearch}
          placeholder="Search or type a command..."
          className={styles.input}
        />
        {isLoading && <div className={styles.spinner} />}
      </div>

      <Command.List className={styles.list}>
        <Command.Empty className={styles.empty}>
          No results found.
        </Command.Empty>

        {/* Search Results */}
        {hasQuery &&
          Object.entries(grouped).map(([type, items]) => (
            <Command.Group
              key={type}
              heading={entityTypeLabels[type as SearchEntityType]}
              className={styles.group}
            >
              {items.map((item) => (
                <Command.Item
                  key={`${item.type}-${item._id}`}
                  value={`${item.type} ${item.label} ${item.subtitle ?? ''}`}
                  onSelect={() => handleSelectResult(item)}
                  className={styles.item}
                >
                  <EntityIcon type={item.type} />
                  <div className={styles.itemContent}>
                    <span className={styles.itemLabel}>{item.label}</span>
                    {item.subtitle && (
                      <span className={styles.itemSubtitle}>
                        {item.subtitle}
                      </span>
                    )}
                  </div>
                </Command.Item>
              ))}
            </Command.Group>
          ))}

        {/* Recent Items (shown when no search query) */}
        {!hasQuery && recents.length > 0 && (
          <Command.Group heading="Recent" className={styles.group}>
            {recents.map((item) => (
              <Command.Item
                key={`recent-${item.type}-${item.id}`}
                value={`recent ${item.label} ${item.subtitle ?? ''}`}
                onSelect={() => handleSelectRecent(item)}
                className={styles.item}
              >
                <ClockIcon height={16} width={16} />
                <div className={styles.itemContent}>
                  <span className={styles.itemLabel}>{item.label}</span>
                  {item.subtitle && (
                    <span className={styles.itemSubtitle}>{item.subtitle}</span>
                  )}
                </div>
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {/* Navigation Commands */}
        <Command.Group heading="Navigation" className={styles.group}>
          {navigationCommands.map((cmd) => (
            <Command.Item
              key={cmd.id}
              value={`${cmd.label} ${cmd.keywords?.join(' ') ?? ''}`}
              onSelect={() => cmd.action(ctx)}
              className={styles.item}
            >
              <span className={styles.itemLabel}>{cmd.label}</span>
            </Command.Item>
          ))}
        </Command.Group>

        {/* Create Commands */}
        <Command.Group heading="Create" className={styles.group}>
          {createCommands.map((cmd) => (
            <Command.Item
              key={cmd.id}
              value={`${cmd.label} ${cmd.keywords?.join(' ') ?? ''}`}
              onSelect={() => cmd.action(ctx)}
              className={styles.item}
            >
              <span className={styles.itemLabel}>{cmd.label}</span>
            </Command.Item>
          ))}
        </Command.Group>

        {/* Quick Actions */}
        <Command.Group heading="Actions" className={styles.group}>
          {actionCommands.map((cmd) => (
            <Command.Item
              key={cmd.id}
              value={`${cmd.label} ${cmd.keywords?.join(' ') ?? ''}`}
              onSelect={() => cmd.action(ctx)}
              className={styles.item}
            >
              <span className={styles.itemLabel}>{cmd.label}</span>
            </Command.Item>
          ))}
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
};
